import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Calendar, User, CheckCircle, Loader, Mail, Lock, Phone, Eye, EyeOff } from 'lucide-react';
import { apartmentService, bookingService, authService } from '../services/api';
import { Apartment } from '../types';
import { useAuthStore } from '../store/authStore';
import BookingCalendar from '../components/BookingCalendar';
import toast from 'react-hot-toast';

// Formate une date en YYYY-MM-DD en utilisant le fuseau horaire local
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const BookingPage: React.FC = () => {
  const { apartmentId } = useParams<{ apartmentId: string }>();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuthStore();
  
  const [apartment, setApartment] = useState<Apartment | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [confirmedDates, setConfirmedDates] = useState<string[]>([]);
  const [pendingDates, setPendingDates] = useState<string[]>([]);
  const [bookingCode, setBookingCode] = useState<string>('');
  const [bookingId, setBookingId] = useState<number | null>(null);
  
  const [bookingData, setBookingData] = useState({
    startDate: null as Date | null,
    endDate: null as Date | null,
    totalPrice: 0,
    nights: 0,
    guestCount: 1,
    message: '',
    paymentType: 'full' as 'full' | 'deposit'  // 'full' = paiement total, 'deposit' = acompte 10%
  });

  // Pré-remplissage depuis la recherche (/booking/:id?date_debut=YYYY-MM-DD&date_fin=YYYY-MM-DD&nb_personnes=N)
  useEffect(() => {
    const dateDebutStr = searchParams.get('date_debut');
    const dateFinStr = searchParams.get('date_fin');
    const nbPersonnesStr = searchParams.get('nb_personnes');

    const parseIsoDate = (s: string) => {
      const [y, m, d] = s.split('-').map((n) => parseInt(n, 10));
      if (!y || !m || !d) return null;
      const dt = new Date(y, m - 1, d);
      return Number.isNaN(dt.getTime()) ? null : dt;
    };

    const start = dateDebutStr ? parseIsoDate(dateDebutStr) : null;
    const end = dateFinStr ? parseIsoDate(dateFinStr) : null;
    const guestCount = nbPersonnesStr ? parseInt(nbPersonnesStr, 10) : NaN;

    setBookingData((prev) => ({
      ...prev,
      ...(start ? { startDate: start } : {}),
      ...(end ? { endDate: end } : {}),
      ...(!Number.isNaN(guestCount) && guestCount > 0 ? { guestCount } : {})
    }));
  }, [searchParams]);

  // Infos d'inscription pour les visiteurs non connectés
  const [registerInfo, setRegisterInfo] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!apartmentId) return;
      try {
        const [aptRes, availRes] = await Promise.all([
          apartmentService.getById(parseInt(apartmentId)),
          apartmentService.getAvailability(parseInt(apartmentId))
        ]);
        setApartment(aptRes.data.apartment);
        
        // Récupérer les différents types de dates
        setBlockedDates(availRes.data.unavailable_dates || []);
        setConfirmedDates(availRes.data.confirmed_dates || []);
        setPendingDates(availRes.data.pending_dates || []);
      } catch (error) {
        console.error(error);
        toast.error('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [apartmentId]);

  const handleDatesSelected = (start: Date | null, end: Date | null, total: number, nights: number) => {
    setBookingData((prev) => ({ ...prev, startDate: start, endDate: end, totalPrice: total, nights }));
  };

  const handleSubmitBooking = async () => {
    if (!bookingData.startDate || !bookingData.endDate || !apartment) {
      toast.error('Veuillez sélectionner des dates');
      return;
    }

    // Si non connecté, utiliser le flux d'inscription avec réservation
    if (!isAuthenticated) {
      // Validation du formulaire d'inscription
      if (!registerInfo.prenom || !registerInfo.nom || !registerInfo.email || !registerInfo.telephone) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      if (!registerInfo.password || registerInfo.password.length < 8) {
        toast.error('Le mot de passe doit contenir au moins 8 caractères');
        return;
      }
      if (registerInfo.password !== registerInfo.confirmPassword) {
        toast.error('Les mots de passe ne correspondent pas');
        return;
      }
    }

    setSubmitting(true);
    try {
      let response;
      const isDeposit = bookingData.paymentType === 'deposit';
      
      if (isAuthenticated) {
        // Réservation avec compte existant
        response = await bookingService.create({
          apartment_id: apartment.id,
          date_debut: formatLocalDate(bookingData.startDate),
          date_fin: formatLocalDate(bookingData.endDate),
          nb_personnes: bookingData.guestCount,
          notes: bookingData.message || undefined,
          is_deposit: isDeposit
        });
      } else {
        // Inscription + Réservation simultanée
        response = await authService.registerWithBooking({
          email: registerInfo.email,
          password: registerInfo.password,
          nom: registerInfo.nom,
          prenom: registerInfo.prenom,
          telephone: registerInfo.telephone,
          booking: {
            apartment_id: apartment.id,
            date_debut: formatLocalDate(bookingData.startDate),
            date_fin: formatLocalDate(bookingData.endDate),
            nb_personnes: bookingData.guestCount,
            notes: bookingData.message || undefined,
            is_deposit: isDeposit
          }
        });
        // Stocker l'email et l'ID de réservation pour la redirection post-validation
        localStorage.setItem('pending_booking_id', response.data.booking?.id?.toString() || '');
        localStorage.setItem('pending_booking_email', registerInfo.email);
        setRegistrationSuccess(true);
        setRegisteredEmail(registerInfo.email);
      }
      setBookingCode(response.data.booking?.code_reservation || '');
      setBookingId(response.data.booking?.id || null);
      toast.success('Réservation envoyée !');
      setStep(3);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de la réservation');
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price: number) => new Intl.NumberFormat('fr-FR').format(price);

  if (loading) {
    return (
      <div className="section">
        <div className="container-app max-w-4xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-neutral-200 rounded w-1/3"></div>
            <div className="h-96 bg-neutral-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!apartment) {
    return (
      <div className="section text-center">
        <p className="text-neutral-600">Appartement non trouvé</p>
        <Link to="/apartments" className="btn btn-primary mt-4">Retour aux appartements</Link>
      </div>
    );
  }

  return (
    <div className="section">
      <div className="container-app max-w-4xl">
        <Link to={`/apartments/${apartment.id}`} className="inline-flex items-center text-neutral-600 hover:text-primary-600 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />Retour à l'appartement
        </Link>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className={`flex items-center ${step >= 1 ? 'text-primary-600' : 'text-neutral-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-primary-500 text-white' : 'bg-neutral-200'}`}>
              <Calendar className="w-5 h-5" />
            </div>
            <span className="ml-2 font-medium hidden sm:inline">Dates</span>
          </div>
          <div className={`w-16 h-0.5 mx-2 ${step >= 2 ? 'bg-primary-500' : 'bg-neutral-200'}`}></div>
          <div className={`flex items-center ${step >= 2 ? 'text-primary-600' : 'text-neutral-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-primary-500 text-white' : 'bg-neutral-200'}`}>
              <User className="w-5 h-5" />
            </div>
            <span className="ml-2 font-medium hidden sm:inline">Coordonnées</span>
          </div>
          <div className={`w-16 h-0.5 mx-2 ${step >= 3 ? 'bg-primary-500' : 'bg-neutral-200'}`}></div>
          <div className={`flex items-center ${step >= 3 ? 'text-primary-600' : 'text-neutral-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-primary-500 text-white' : 'bg-neutral-200'}`}>
              <CheckCircle className="w-5 h-5" />
            </div>
            <span className="ml-2 font-medium hidden sm:inline">Terminé</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {step === 1 && (
              <div className="card p-6">
                <h2 className="text-xl font-semibold mb-6">Sélectionnez vos dates</h2>
                <BookingCalendar
                  apartmentId={apartment.id}
                  pricePerNight={apartment.prix_par_nuit}
                  cleaningFee={apartment.frais_menage || 0}
                  blockedDates={blockedDates}
                  confirmedDates={confirmedDates}
                  pendingDates={pendingDates}
                  initialStartDate={bookingData.startDate}
                  initialEndDate={bookingData.endDate}
                  onDatesSelected={handleDatesSelected}
                />
                <div className="mt-6">
                  <label className="label">Nombre de personnes</label>
                  <select
                    value={bookingData.guestCount}
                    onChange={(e) => setBookingData((prev) => ({ ...prev, guestCount: parseInt(e.target.value) }))}
                    className="input"
                  >
                    {[...Array(apartment.capacite)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1} personne{i > 0 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => setStep(2)}
                  disabled={!bookingData.startDate || !bookingData.endDate}
                  className="btn btn-primary w-full mt-6"
                >
                  Continuer
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="card p-6">
                <h2 className="text-xl font-semibold mb-6">Vos coordonnées</h2>
                <div className="space-y-4">
                  {/* Formulaire d'inscription si non connecté */}
                  {!isAuthenticated && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <p className="text-blue-800 text-sm mb-4">
                        <User className="w-4 h-4 inline mr-1" />
                        Créez votre compte pour finaliser votre réservation. Vous devrez activer votre compte via email avant de procéder au paiement.
                      </p>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="label">Prénom *</label>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                              <input
                                type="text"
                                value={registerInfo.prenom}
                                onChange={(e) => setRegisterInfo({ ...registerInfo, prenom: e.target.value })}
                                className="input pl-10"
                                placeholder="Jean"
                                required
                              />
                            </div>
                          </div>
                          <div>
                            <label className="label">Nom *</label>
                            <input
                              type="text"
                              value={registerInfo.nom}
                              onChange={(e) => setRegisterInfo({ ...registerInfo, nom: e.target.value })}
                              className="input"
                              placeholder="Dupont"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <label className="label">Email *</label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                            <input
                              type="email"
                              value={registerInfo.email}
                              onChange={(e) => setRegisterInfo({ ...registerInfo, email: e.target.value })}
                              className="input pl-10"
                              placeholder="jean@example.com"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <label className="label">Téléphone *</label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                            <input
                              type="tel"
                              value={registerInfo.telephone}
                              onChange={(e) => setRegisterInfo({ ...registerInfo, telephone: e.target.value })}
                              className="input pl-10"
                              placeholder="+221 77 123 45 67"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <label className="label">Mot de passe * (min. 8 caractères)</label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={registerInfo.password}
                              onChange={(e) => setRegisterInfo({ ...registerInfo, password: e.target.value })}
                              className="input pl-10 pr-10"
                              placeholder="••••••••"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                            >
                              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="label">Confirmer le mot de passe *</label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={registerInfo.confirmPassword}
                              onChange={(e) => setRegisterInfo({ ...registerInfo, confirmPassword: e.target.value })}
                              className="input pl-10"
                              placeholder="••••••••"
                              required
                            />
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-blue-700 mt-3">
                        Vous avez déjà un compte ? <Link to="/login" className="font-medium underline">Connectez-vous</Link> pour réserver plus rapidement.
                      </p>
                    </div>
                  )}

                  {isAuthenticated && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <p className="text-green-800 text-sm">
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Connecté en tant que <strong>{user?.prenom} {user?.nom}</strong> ({user?.email})
                      </p>
                    </div>
                  )}

                  {/* Choix du mode de paiement */}
                  <div className="border-2 border-neutral-200 rounded-lg p-4">
                    <h3 className="font-medium mb-3">Mode de paiement</h3>
                    <div className="space-y-3">
                      <label className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        bookingData.paymentType === 'full' 
                          ? 'border-primary-500 bg-primary-50' 
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}>
                        <input
                          type="radio"
                          name="paymentType"
                          value="full"
                          checked={bookingData.paymentType === 'full'}
                          onChange={() => setBookingData({ ...bookingData, paymentType: 'full' })}
                          className="mt-1 mr-3"
                        />
                        <div className="flex-1">
                          <span className="font-medium">Paiement total</span>
                          <p className="text-sm text-neutral-600 mt-1">
                            Payez le montant total maintenant: <strong>{formatPrice(bookingData.totalPrice)} FCFA</strong>
                          </p>
                        </div>
                      </label>
                      
                      <label className={`flex items-start p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        bookingData.paymentType === 'deposit' 
                          ? 'border-primary-500 bg-primary-50' 
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}>
                        <input
                          type="radio"
                          name="paymentType"
                          value="deposit"
                          checked={bookingData.paymentType === 'deposit'}
                          onChange={() => setBookingData({ ...bookingData, paymentType: 'deposit' })}
                          className="mt-1 mr-3"
                        />
                        <div className="flex-1">
                          <span className="font-medium">Acompte de 10%</span>
                          <p className="text-sm text-neutral-600 mt-1">
                            Versez un acompte de <strong>{formatPrice(Math.round(bookingData.totalPrice * 0.10))} FCFA</strong> maintenant,
                            le reste ({formatPrice(bookingData.totalPrice - Math.round(bookingData.totalPrice * 0.10))} FCFA) avant l'arrivée.
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Une facture PDF sera générée après validation par le gérant
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="label">Message pour le gérant (optionnel)</label>
                    <textarea
                      value={bookingData.message}
                      onChange={(e) => setBookingData({ ...bookingData, message: e.target.value })}
                      className="input"
                      rows={3}
                      placeholder="Heure d'arrivée prévue, demandes spéciales..."
                    ></textarea>
                  </div>
                  <div className="bg-neutral-50 p-4 rounded-lg">
                    <h3 className="font-medium mb-2">Récapitulatif</h3>
                    <div className="text-sm space-y-1">
                      <p><span className="text-neutral-600">Arrivée:</span> {bookingData.startDate?.toLocaleDateString('fr-FR')}</p>
                      <p><span className="text-neutral-600">Départ:</span> {bookingData.endDate?.toLocaleDateString('fr-FR')}</p>
                      <p><span className="text-neutral-600">Durée:</span> {bookingData.nights} nuit{bookingData.nights > 1 ? 's' : ''}</p>
                      <p><span className="text-neutral-600">Voyageurs:</span> {bookingData.guestCount}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 mt-6">
                  <button onClick={() => setStep(1)} className="btn btn-outline flex-1">Retour</button>
                  <button onClick={handleSubmitBooking} disabled={submitting} className="btn btn-primary flex-1">
                    {submitting ? <><Loader className="w-4 h-4 mr-2 animate-spin" />Envoi...</> : 'Confirmer la réservation'}
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="card p-8 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold mb-2">Réservation enregistrée !</h2>
                {bookingCode && (
                  <p className="text-lg font-mono bg-neutral-100 inline-block px-4 py-2 rounded mb-4">
                    {bookingCode}
                  </p>
                )}
                
                {isAuthenticated ? (
                  <>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                      <p className="text-amber-800 font-medium mb-2">
                        ⏳ Votre réservation est en attente de paiement
                      </p>
                      <p className="text-amber-700 text-sm">
                        Pour confirmer définitivement votre réservation, veuillez procéder au paiement.
                        Le gérant a été notifié de votre demande.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      {bookingId && (
                        <Link to={`/locataire/bookings/${bookingId}`} className="btn btn-primary">
                          Procéder au paiement
                        </Link>
                      )}
                      <Link to="/locataire/bookings" className="btn btn-outline">Mes réservations</Link>
                    </div>
                  </>
                ) : registrationSuccess ? (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                      <p className="text-green-800 font-medium mb-2">
                        📧 Activez votre compte pour continuer
                      </p>
                      <p className="text-green-700 text-sm mb-3">
                        Votre compte a été créé et votre réservation est enregistrée !
                        Un email de validation a été envoyé à <strong>{registeredEmail}</strong>.
                      </p>
                      <p className="text-green-700 text-sm">
                        Cliquez sur le lien dans l'email pour activer votre compte, puis connectez-vous 
                        pour accéder à votre espace personnel et procéder au paiement.
                      </p>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                      <p className="text-amber-700 text-sm">
                        💡 <strong>Conseil :</strong> Vérifiez également votre dossier spam si vous ne trouvez pas l'email.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Link to="/login" className="btn btn-primary">Se connecter après activation</Link>
                      <Link to="/" className="btn btn-outline">Retour à l'accueil</Link>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <p className="text-blue-800 font-medium mb-2">
                        🔐 Connectez-vous pour finaliser
                      </p>
                      <p className="text-blue-700 text-sm">
                        Votre demande a été enregistrée. Connectez-vous ou créez un compte 
                        pour procéder au paiement et confirmer définitivement votre réservation.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Link to="/login" className="btn btn-primary">Se connecter / Créer un compte</Link>
                      <Link to="/" className="btn btn-outline">Retour à l'accueil</Link>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="card p-4 sticky top-24">
              <div className="flex gap-4 mb-4">
                {apartment.photos && apartment.photos[0] ? (
                  <img 
                    src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${apartment.photos[0].url}`}
                    alt={apartment.titre}
                    className="w-20 h-20 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 bg-neutral-200 rounded-lg"></div>
                )}
                <div>
                  <h3 className="font-semibold line-clamp-2">{apartment.titre}</h3>
                  <p className="text-sm text-neutral-500">{apartment.ville}</p>
                </div>
              </div>
              <hr className="my-4" />
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-600">Prix/nuit</span>
                  <span>{formatPrice(apartment.prix_par_nuit)} FCFA</span>
                </div>
                {bookingData.nights > 0 && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-neutral-600">{bookingData.nights} nuit{bookingData.nights > 1 ? 's' : ''}</span>
                      <span>{formatPrice(apartment.prix_par_nuit * bookingData.nights)} FCFA</span>
                    </div>
                    {apartment.frais_menage && apartment.frais_menage > 0 && (
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Frais ménage</span>
                        <span>{formatPrice(apartment.frais_menage)} FCFA</span>
                      </div>
                    )}
                    <hr className="my-2" />
                    <div className="flex justify-between font-semibold">
                      <span>Total</span>
                      <span className="text-primary-600">{formatPrice(bookingData.totalPrice)} FCFA</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
